import { useRef, useState } from "react";
import { toast } from "sonner";
import {
    Image as ImageIcon,
    Upload,
    Trash2,
    Palette,
    Sparkles,
    Type,
    Loader2,
    Check,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { compressImageIfNeeded } from "../lib/imageCompress";
import {
    BRAND_PALETTES,
    BRAND_STYLES,
    DEFAULT_PALETTE_ID,
    DEFAULT_STYLE_ID,
} from "../lib/brandStyles";

const MAX_LOGO_BYTES = 200_000;
const LOGO_TARGET_PX = 640;

/** Card "Personalizza il tuo brand" mostrata in Dashboard. */
export default function RestaurantBrandCard() {
    const { user, setUser } = useAuth();
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [savingBrand, setSavingBrand] = useState(false);

    const brandColor = user?.brand_color || DEFAULT_PALETTE_ID;
    const brandStyle = user?.brand_style || DEFAULT_STYLE_ID;
    const hasLogo = !!user?.restaurant_logo_url;

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Carica un'immagine PNG o JPEG");
            return;
        }
        setUploading(true);
        try {
            // 1. Comprimi a max 640px e qualità alta (logo deve restare nitido)
            const compressed = await compressImageIfNeeded(file, {
                maxDimension: LOGO_TARGET_PX,
                quality: 0.9,
                thresholdMB: 0,
            });
            // 2. Se ancora troppo grande, comprimi di più
            let finalFile = compressed;
            if (finalFile.size > MAX_LOGO_BYTES) {
                finalFile = await compressImageIfNeeded(file, {
                    maxDimension: 480,
                    quality: 0.75,
                    thresholdMB: 0,
                });
            }
            if (finalFile.size > MAX_LOGO_BYTES) {
                toast.error(
                    "Logo troppo grande anche dopo la compressione. Usa un'immagine più piccola."
                );
                return;
            }
            // 3. Converti in data URL base64
            const dataUrl = await fileToDataUrl(finalFile);
            const { data } = await api.post("/auth/logo", { data_url: dataUrl });
            setUser(data);
            toast.success("Logo caricato!");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const removeLogo = async () => {
        setRemoving(true);
        try {
            const { data } = await api.delete("/auth/logo");
            setUser(data);
            toast.success("Logo rimosso");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRemoving(false);
        }
    };

    const updateBranding = async (patch) => {
        setSavingBrand(true);
        try {
            const { data } = await api.put("/auth/branding", patch);
            setUser(data);
            toast.success("Preferenze salvate");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSavingBrand(false);
        }
    };

    return (
        <section
            className="bg-paper rounded-xl border border-brand-line shadow-card p-5 sm:p-6"
            data-testid="brand-card"
        >
            <div className="flex items-center gap-2 mb-1">
                <Sparkles size={14} className="text-brand-gold" />
                <div className="eyebrow text-brand-ink/70">Personalizza il tuo brand</div>
            </div>
            <h2 className="font-display text-2xl text-brand-ink mb-4">
                Logo, colori e stile per i PDF del menu
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
                {/* Logo upload */}
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-brand-muted font-semibold mb-2">
                        Logo del ristorante
                    </div>
                    <div className="rounded-xl border border-dashed border-brand-line bg-brand-cream/40 h-44 flex items-center justify-center overflow-hidden">
                        {hasLogo ? (
                            <img
                                src={user.restaurant_logo_url}
                                alt="Logo ristorante"
                                className="max-h-40 max-w-[230px] object-contain"
                                data-testid="brand-logo-preview"
                            />
                        ) : (
                            <div className="text-center text-brand-muted px-4">
                                <ImageIcon size={28} className="mx-auto mb-1.5 opacity-60" />
                                <div className="text-[12px]">
                                    Nessun logo caricato.
                                    <br />
                                    PNG/JPEG, max 200KB.
                                </div>
                            </div>
                        )}
                    </div>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        className="hidden"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        data-testid="brand-logo-input"
                    />
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            disabled={uploading}
                            data-testid="brand-logo-upload"
                            className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy text-white px-3.5 py-1.5 text-[12px] font-bold hover:bg-brand-navyLight transition disabled:opacity-60"
                        >
                            {uploading ? (
                                <>
                                    <Loader2 size={12} className="animate-spin" />
                                    Caricamento…
                                </>
                            ) : (
                                <>
                                    <Upload size={12} />
                                    {hasLogo ? "Sostituisci" : "Carica logo"}
                                </>
                            )}
                        </button>
                        {hasLogo && (
                            <button
                                type="button"
                                onClick={removeLogo}
                                disabled={removing}
                                data-testid="brand-logo-remove"
                                className="inline-flex items-center gap-1.5 rounded-full border border-brand-line text-brand-muted hover:text-brand-danger hover:border-brand-danger/60 px-3 py-1.5 text-[12px] font-semibold transition"
                            >
                                <Trash2 size={11} />
                                Rimuovi
                            </button>
                        )}
                    </div>
                </div>

                {/* Brand color + style defaults */}
                <div className="space-y-5">
                    <div>
                        <div className="flex items-center gap-1.5 mb-2">
                            <Palette size={12} className="text-brand-gold" />
                            <div className="text-[10px] uppercase tracking-wider text-brand-muted font-semibold">
                                Palette colori preferita
                            </div>
                        </div>
                        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                            {BRAND_PALETTES.map((p) => {
                                const active = brandColor === p.id;
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() =>
                                            updateBranding({ brand_color: p.id })
                                        }
                                        disabled={savingBrand}
                                        data-testid={`brand-card-palette-${p.id}`}
                                        title={p.label}
                                        className={`relative aspect-square rounded-lg border transition flex items-center justify-center ${
                                            active
                                                ? "border-brand-navy shadow scale-105"
                                                : "border-brand-line hover:border-brand-gold/60"
                                        }`}
                                        style={{
                                            background: `linear-gradient(135deg, ${p.primary} 0%, ${p.primary} 60%, ${p.accent} 60%, ${p.accent} 100%)`,
                                        }}
                                    >
                                        {active && (
                                            <Check
                                                size={13}
                                                className="text-white drop-shadow"
                                            />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-1.5 text-[11px] text-brand-muted">
                            Selezionata:{" "}
                            <span className="text-brand-ink font-semibold">
                                {BRAND_PALETTES.find((p) => p.id === brandColor)?.label}
                            </span>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center gap-1.5 mb-2">
                            <Type size={12} className="text-brand-gold" />
                            <div className="text-[10px] uppercase tracking-wider text-brand-muted font-semibold">
                                Stile grafico preferito
                            </div>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                            {BRAND_STYLES.map((s) => {
                                const active = brandStyle === s.id;
                                return (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={() =>
                                            updateBranding({ brand_style: s.id })
                                        }
                                        disabled={savingBrand}
                                        data-testid={`brand-card-style-${s.id}`}
                                        className={`text-left rounded-lg border px-2.5 py-2 transition ${
                                            active
                                                ? "border-brand-navy bg-paper shadow"
                                                : "border-brand-line hover:border-brand-gold/60"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between gap-1">
                                            <div className="font-display text-sm text-brand-ink">
                                                {s.label}
                                            </div>
                                            {active && (
                                                <Check
                                                    size={11}
                                                    className="text-brand-navy"
                                                />
                                            )}
                                        </div>
                                        <div className="text-[10px] text-brand-muted leading-tight mt-0.5 line-clamp-2">
                                            {s.description}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Lettura file fallita"));
        reader.readAsDataURL(file);
    });
}
